import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, Clock, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

const blogPosts = [
  {
    id: 1,
    title: '10 SEO Strategies That Will Dominate 2024',
    excerpt: 'Discover the latest SEO techniques and best practices that will help your website rank higher and attract more organic traffic in the new year.',
    image: 'https://images.unsplash.com/photo-1709281847780-2b34c28853c0',
    category: 'SEO',
    author: 'Square Trigger Team',
    date: 'Jan 15, 2024',
    readTime: '8 min read'
  },
  {
    id: 2,
    title: 'The Future of Web Development: Trends to Watch',
    excerpt: 'From AI integration to progressive web apps, explore the technologies shaping the future of web development and how they can benefit your business.',
    image: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613',
    category: 'Development',
    author: 'Square Trigger Team',
    date: 'Jan 10, 2024',
    readTime: '6 min read'
  },
  {
    id: 3,
    title: 'How to Build a High-Converting E-Commerce Site',
    excerpt: 'Learn the essential elements and strategies for creating an e-commerce website that turns visitors into loyal customers.',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166',
    category: 'E-Commerce',
    author: 'Square Trigger Team',
    date: 'Jan 5, 2024',
    readTime: '10 min read'
  },
  {
    id: 4,
    title: 'Social Media Marketing: A Complete Guide',
    excerpt: 'Master the art of social media marketing with our comprehensive guide covering strategy, content creation, and analytics.',
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113',
    category: 'Marketing',
    author: 'Square Trigger Team',
    date: 'Dec 28, 2023',
    readTime: '12 min read'
  },
  {
    id: 5,
    title: 'UI/UX Design Principles Every Developer Should Know',
    excerpt: 'Understanding these fundamental design principles will help you create more user-friendly and visually appealing websites.',
    image: 'https://images.unsplash.com/photo-1541462608143-67571c6738dd',
    category: 'Design',
    author: 'Square Trigger Team',
    date: 'Dec 20, 2023',
    readTime: '7 min read'
  },
  {
    id: 6,
    title: 'Why Your Business Needs a Digital Strategy',
    excerpt: 'In today\'s digital-first world, having a comprehensive digital strategy is no longer optional. Here\'s why and how to create one.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40',
    category: 'Strategy',
    author: 'Square Trigger Team',
    date: 'Dec 15, 2023',
    readTime: '9 min read'
  }
];

const categories = ['All', 'SEO', 'Development', 'E-Commerce', 'Marketing', 'Design', 'Strategy'];

export default function BlogPage() {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState('All');

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              Our Blog
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              Insights &{' '}
              <span className="gradient-text">Resources</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Stay updated with the latest trends, tips, and insights in digital marketing, web development, and design.
            </p>

            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-border">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link to={`/blog/${post.id}`} className="block group">
                    <Card className="overflow-hidden border-border hover:border-primary/30 transition-all duration-300 card-hover h-full flex flex-col">
                      {/* Image */}
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute top-4 left-4">
                          <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">
                            {post.category}
                          </Badge>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow">
                        <h2 className="text-xl font-display font-semibold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-grow">
                          {post.excerpt}
                        </p>

                        {/* Meta */}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-4 border-t border-border mt-auto">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.readTime}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4">
              Subscribe to Our Newsletter
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Get the latest insights delivered straight to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="bg-background/10 border-background/20 text-background placeholder:text-background/50"
              />
              <Button variant="premium" className="bg-background text-foreground hover:bg-background/90">
                Subscribe
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
